import { useState, useEffect, useRef } from 'react';

export const useModules = (modules, position, canvasSize, currentUser) => {
  const [localModules, setLocalModules] = useState([]);
  const [activeModule, setActiveModule] = useState(null);
  const modulesLoaded = useRef(false);

  // Try to get modules from debug info if props are empty - only once
  useEffect(() => {
    if (modulesLoaded.current) return;

    if (modules && Array.isArray(modules) && modules.length > 0) {
      setLocalModules(modules);
      modulesLoaded.current = true;
    } else {
      console.log("Modules array is empty, trying to get data from debug element");
      const debugElement = document.getElementById('debug-info');
      if (debugElement) {
        try {
          const debugData = JSON.parse(debugElement.textContent);
          console.log("Found modules in debug element:", debugData);
          setLocalModules(debugData);
          modulesLoaded.current = true;
        } catch (e) {
          console.error("Error parsing debug data:", e);
        }
      }
    }
  }, [modules]);

  // Calculate module positions based on canvas size
  const getModulePositions = () => {
    if (!localModules || localModules.length === 0) return [];

    // Regular learning modules positions
    const positions = [
      { x: canvasSize.width * 0.2, y: canvasSize.height * 0.3 },
      { x: canvasSize.width * 0.5, y: canvasSize.height * 0.2 },
      { x: canvasSize.width * 0.8, y: canvasSize.height * 0.3 },
      { x: canvasSize.width * 0.2, y: canvasSize.height * 0.7 },
      { x: canvasSize.width * 0.5, y: canvasSize.height * 0.8 },
      { x: canvasSize.width * 0.8, y: canvasSize.height * 0.7 },
    ].slice(0, localModules.length);

    // Only add rewards module for authenticated users
    if (currentUser) {
      positions.push({ x: canvasSize.width * 0.9, y: canvasSize.height * 0.9, isRewards: true });
    }

    return positions;
  };

  // Check if near any module
  useEffect(() => {
    if (!localModules || localModules.length === 0) return;

    const modulePositions = getModulePositions();
    let nearestModule = null;
    let nearestDistance = Infinity;

    modulePositions.forEach((modulePos, index) => {
      const dx = position.x - modulePos.x;
      const dy = position.y - modulePos.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Use a smaller interaction radius for the rewards module
      const interactionRadius = modulePos.isRewards ? 80 : 100;

      if (distance < interactionRadius && distance < nearestDistance) {
        nearestModule = index;
        nearestDistance = distance;
      }
    });

    if (nearestModule !== activeModule) {
      setActiveModule(nearestModule);
    }
  }, [position, localModules, canvasSize, activeModule]);

  const handleModuleClick = () => {
    if (activeModule !== null && localModules[activeModule]) {
      const module = localModules[activeModule];

      // For non-authenticated users, just navigate to the target URL without gem multipliers
      if (!currentUser) {
        window.location.href = module.path;
        return;
      }

      // For authenticated users, include gem multipliers
      const gemCount = window.collectedGems || 0;
      const multiplier = Math.max(1, Math.floor(gemCount / 10) + 1);

      // Add the multiplier as a URL parameter
      let targetUrl = module.path;
      const separator = targetUrl.includes('?') ? '&' : '?';
      targetUrl = `${targetUrl}${separator}gem_multiplier=${multiplier}&gems=${gemCount}`;

      // Navigate to the module with the multiplier
      window.location.href = targetUrl;
    }
  };

  return {
    localModules,
    activeModule,
    setActiveModule,
    getModulePositions,
    handleModuleClick
  };
};